import React, { useRef, useState, useEffect } from 'react';
import { Download, X, Copy, Check } from 'lucide-react';
import Lottie from 'lottie-react';
import { getDisburseDataByLoanId } from '../../../services/loanApi';
import { LOAN_APIS } from '../../../api/loan-api-factory';
import axios from 'axios';
import generateDisbursalOrderReceipt from './generateDisbursalOrderReceipt';
import { copyTextToClipboard } from '../../../utils/clipboard';
import writingAnimation from '../animations/writing-on-paper.json';
import './DisbursalOrderOverlay.css';

// Helper function to format currency (Careena template)
const formatCurrency = (val) =>
  val != null && val !== '' ? `₹ ${Number(val).toLocaleString('en-IN')}` : '₹ 0';

// Helper function to map unified API response to receipt data
const mapDisbursementDataToOrderData = (data) => {
  const calc = data.loanCalculationDetails || {};

  return {
    orderId: data.applicationId,
    merchantName: data.clinicName || data.businessEntityName,
    patientName: data.patientName ?? data.customerName ?? data.firstName,
    treatmentName: data.treatmentName ?? data.loanReason,
    customerName: data.customerName ?? data.firstName,
    contactNumber: data.customerNumber ?? data.mobileNumber,
    transactionAmount: formatCurrency(data.treatmentAmount ?? calc.treatmentAmount),
    paymentPlan: `${data.totalEmi ?? data.totalTenure ?? calc.totalTenure ?? ''}/${data.advanceEmi ?? 0}`,
    advanceAmount: formatCurrency(data.advanceEmiAmount),
    platformCharges: formatCurrency(
      data.platformCharges ?? data.subventionExcludingGst ?? data.subventionExcludingGSt ?? calc.subventionExcludingGSt
    ),
    gst: formatCurrency(data.gstOnPlatformCharges ?? data.gstOnSubvention ?? calc.gstOnSubvention),
    tcsAmountRaw: data.tcsAmount ?? calc.tcsAmount ?? 0,
    tcsAmount: formatCurrency(data.tcsAmount ?? calc.tcsAmount),
    paymentToMerchant: formatCurrency(
      data.paymentToMerchant ?? data.disburseAmount ?? calc.disburseAmount ?? calc.paymentToMerchant
    ),
    pfAmount: formatCurrency(data.pf ?? data.processingFee ?? calc.processingFee),
    financierName: data.nbfc ?? data.nbfcName,
    utr: data.utr ?? data.utrNo ?? data.utrNumber ?? '',
    doSharedTimeStamp: data.doSharedTimeStamp,
  };
};

const DisbursalOrderOverlay = ({ loanId, onClose }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disbursalOrderData, setDisbursalOrderData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [utrCopied, setUtrCopied] = useState(false);
  const [showSlideUp, setShowSlideUp] = useState(false);
  const contentRef = useRef(null);

  // Fetch disbursement data on component mount
  useEffect(() => {
    const fetchDisbursementData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setLoadingProgress(0);

        // Update DO timestamp before generating the disbursal order
        await axios.get(LOAN_APIS.UPDATE_DO_TIMESTAMP(), {
          params: {
            loanId,
            sharedBy: localStorage.getItem('emailId') || sessionStorage.getItem('emailId') || '',
          },
        }).catch(() => {});
        
        // Simulate progress updates over 2 seconds to reach 100%
        const progressInterval = setInterval(() => {
          setLoadingProgress(prev => {
            if (prev >= 100) return 100;
            return prev + (100 / 20); // Increment by 5% every 100ms for 2 seconds total
          });
        }, 100);
        
        const result = await getDisburseDataByLoanId(loanId);
        
        // Complete the progress to 100%
        setLoadingProgress(100);
        clearInterval(progressInterval);
        
        // Wait for 2 seconds total for the loading animation to complete
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        
        if (result.success && result.data) {
          const mappedData = mapDisbursementDataToOrderData(result.data);
          await new Promise(resolve => setTimeout(resolve, 500));
          setDisbursalOrderData(mappedData);
          setShowSlideUp(true);
        } else {
          setError(result.message || 'Failed to fetch disbursement data');
        }
      } catch (err) {
        console.error('Error fetching disbursement data:', err);
        setError(err.message || 'An error occurred while fetching disbursement data');
      } finally {
        setIsLoading(false);
      }
    };

    if (loanId) {
      fetchDisbursementData();
    }
  }, [loanId]);



  // Download as PDF function
  const downloadAsPDF = async () => {
    if (!disbursalOrderData) return;
    
    setIsDownloading(true);
    try {
      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default;
      
      const element = contentRef.current;
      if (!element) return;

      const opt = {
        margin: 0,
        filename: `DisbursalOrder${disbursalOrderData.orderId}`,
        image: { type: 'jpeg', quality: 0.5 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        },
        jsPDF: {
          unit: 'pt',
          format: [element.scrollWidth, element.scrollHeight],
          orientation: 'portrait',
        },
      };

      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error generating PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyUtr = async () => {
    if (!disbursalOrderData?.utr) return;
    const copied = await copyTextToClipboard(disbursalOrderData.utr);
    if (copied) {
      setUtrCopied(true);
      setTimeout(() => setUtrCopied(false), 2000);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="disbursal-overlay">
        {/* Backdrop */}
        <div 
          className="disbursal-backdrop"
          onClick={onClose}
        />
        
        {/* Bottom Sheet / Modal */}
        <div className="disbursal-modal">
          {/* Header */}
          <div className="disbursal-header">
            <div className="disbursal-header-content">
              <div className="disbursal-header-left">
                <h3 className="disbursal-header-title">Disbursal Order</h3>
              </div>
              <button 
                onClick={onClose} 
                className="disbursal-close-btn"
              >
                <X style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            </div>
          </div>
          
          {/* Loading Content */}
          <div className="disbursal-loading-content">
            <div className="disbursal-loading-wrapper">
              {/* Lottie Writing Animation */}
              <div className="disbursal-lottie-container">
                <Lottie
                  animationData={writingAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
              
              <div className="disbursal-loading-text">
                <h3 className="disbursal-loading-title">Generating Disbursal Order</h3>
                <p className="disbursal-loading-subtitle">
                  {loadingProgress < 30 ? 'Fetching disbursement data...' :
                   loadingProgress < 60 ? 'Processing document details...' :
                   loadingProgress < 90 ? 'Finalizing disbursal order...' :
                   loadingProgress < 100 ? 'Almost ready...' :
                   'Disbursal Order Generated Successfully!'}
                </p>
                <div className="disbursal-progress-info">
                  <span>{Math.round(loadingProgress)}% complete</span>
                  {loadingProgress >= 100 && (
                    <div className="success-animation">
                      <svg style={{ width: '1rem', height: '1rem', color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Enhanced Progress indicator */}
              <div className="disbursal-progress-bar-container">
                <div 
                  className={`disbursal-progress-bar ${loadingProgress >= 100 ? 'completed' : ''}`}
                  style={{ width: `${loadingProgress}%` }}
                >
                  <div className={`disbursal-progress-shimmer ${loadingProgress >= 100 ? 'completed' : ''}`}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="disbursal-overlay">
        {/* Backdrop */}
        <div 
          className="disbursal-backdrop"
          onClick={onClose}
        />
        
        {/* Bottom Sheet / Modal */}
        <div className="disbursal-modal">
          {/* Header */}
          <div className="disbursal-header">
            <div className="disbursal-header-content">
              <div className="disbursal-header-left">
                <h3 className="disbursal-header-title">Disbursal Order</h3>
              </div>
              <button 
                onClick={onClose} 
                className="disbursal-close-btn"
              >
                <X style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            </div>
          </div>
          
          {/* Error Content */}
          <div className="disbursal-error-content">
            <div className="disbursal-error-wrapper">
              <div className="disbursal-error-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="disbursal-error-title">Error Loading Disbursal Order</h3>
              <p className="disbursal-error-message">{error}</p>
              <div className="disbursal-error-buttons">
                <button
                  onClick={onClose}
                  className="disbursal-btn disbursal-btn-close"
                >
                  Close
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="disbursal-btn disbursal-btn-retry"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show main content when data is loaded
  if (!disbursalOrderData) {
    return null;
  }

  return (
    <div className="disbursal-overlay">
      {/* Backdrop */}
      <div 
        className="disbursal-backdrop"
        onClick={onClose}
      />
      
      {/* Bottom Sheet / Modal */}
      <div className={`disbursal-modal-content ${showSlideUp ? 'animate-slideUp' : ''}`}>
        {/* Header */}
        <div className="disbursal-header">
          <div className="disbursal-header-content">
            <div className="disbursal-header-left">
              <h3 className="disbursal-header-title">
                Disbursal Order - {disbursalOrderData.orderId}
              </h3>
            </div>
            <button 
              onClick={onClose} 
              className="disbursal-close-btn"
            >
              <X style={{ width: '1.25rem', height: '1.25rem' }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="disbursal-content">
          <div className="disbursal-content-wrapper">
            <div 
              ref={contentRef}
              className="disbursal-receipt-container"
              style={{ 
                width: '100%',
                minHeight: 'fit-content',
                maxHeight: '100%'
              }}
            >
              {generateDisbursalOrderReceipt(disbursalOrderData)}
            </div>
          </div>
        </div>

        {/* Bottom Download Button */}
        <div className="disbursal-download-section">
          {disbursalOrderData.utr && (
            <div className="disbursal-utr-row">
              <div className="disbursal-utr-copy-text">
                <span>UTR number</span>
                <strong>{disbursalOrderData.utr}</strong>
              </div>
              <button
                type="button"
                onClick={handleCopyUtr}
                className={`disbursal-utr-copy-btn${utrCopied ? ' copied' : ''}`}
                aria-label={utrCopied ? 'UTR number copied' : 'Copy UTR number'}
                title={utrCopied ? 'Copied' : 'Copy UTR number'}
              >
                {utrCopied ? <Check style={{ width: '0.9rem', height: '0.9rem' }} /> : <Copy style={{ width: '0.9rem', height: '0.9rem' }} />}
                {utrCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
          <button
            onClick={downloadAsPDF}
            disabled={isDownloading}
            className="disbursal-download-btn"
          >
            {isDownloading ? (
              <>
                <div className="disbursal-spinner"></div>
                <span>Generating PDF...</span>
              </>
            ) : (
              <>
                <Download style={{ width: '1.25rem', height: '1.25rem' }} />
                <span>Download PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisbursalOrderOverlay;

